import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Hash, MessageSquare, Ship, MoreHorizontal } from 'lucide-react';
import { useUnread } from '../context/UnreadContext';
import MobileMoreSheet from './MobileMoreSheet';

const NAV_ITEMS = [
  { icon: Home, label: 'Accueil', path: '/' },
  { icon: Hash, label: 'Channels', path: '/channels', badge: 'channels' },
  { icon: MessageSquare, label: 'Messages', path: '/dm', badge: 'dm' },
  { icon: Ship, label: 'Odyssée', path: '/progression' },
];

/** Barre de navigation basse (mobile uniquement). */
const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { total: totalUnread, dmTotal } = useUnread();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActivePath = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-border bg-card/95 backdrop-blur-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-stretch justify-around h-16">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            const badgeCount = item.badge === 'channels' ? totalUnread : item.badge === 'dm' ? dmTotal : 0;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`relative flex flex-col items-center justify-center flex-1 min-w-[44px] gap-1 transition-colors active:bg-secondary/60 ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {isActive && (
                  <motion.div
                    layoutId="mobile-nav-indicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-[3px] bg-primary rounded-b-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
                <span className="relative">
                  <Icon className="w-[22px] h-[22px]" />
                  {badgeCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                      style={{ background: 'hsl(var(--red-dossier))', color: '#fff' }}
                      data-testid={`mobile-nav-badge-${item.label.toLowerCase()}`}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
              </button>
            );
          })}

          <button
            onClick={() => setMoreOpen(true)}
            className="relative flex flex-col items-center justify-center flex-1 min-w-[44px] gap-1 text-muted-foreground transition-colors active:bg-secondary/60"
            data-testid="mobile-nav-plus"
          >
            <MoreHorizontal className="w-[22px] h-[22px]" />
            <span className="text-[10px] font-medium leading-none">Plus</span>
          </button>
        </div>
      </nav>

      <MobileMoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  );
};

export default MobileNav;
